import { DialogForm, Form, useDialogExitWarning } from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { MutateVirtualAtonFormValues } from './atonForm';
import {
  DrawingStyles,
  useDrawingContext,
  useResetDrawingContext,
} from '@ais-ui/react-openlayers';
import { MutateVirtualForm } from './MutateVirtualForm';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShareGroups } from '@ais-ui/websockets';
import { AtoN } from '@ais-ui/api-spec/config';
import { AtoNWithDate } from '@ais-ui/api';
import { useDrawWithViewMode } from '../../../hooks/useDrawWithViewMode';

interface AtonDialogProps {
  form: UseFormReturn<MutateVirtualAtonFormValues>;
  onSubmit: (data: MutateVirtualAtonFormValues) => void;
  mutateType: 'edit' | 'create';
  isSubmitting: boolean;
  setShowOuterDialog: (showOuterDialog: boolean) => void;
  aton?: AtoN | AtoNWithDate | null;
}

export const AtonVirtualDialog = ({
  form,
  onSubmit,
  mutateType,
  isSubmitting,
  setShowOuterDialog,
  aton,
}: AtonDialogProps) => {
  const { messages } = useLocalization();
  const dialogTitle =
    mutateType === 'create'
      ? messages.atonVirtualDialogCreateTitle
      : messages.atonVirtualDialogEditTitle;

  const [showDialog, setShowDialog] = useState<boolean>(true);
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const { showDrawPanel } = useDrawingContext();
  const resetDrawingContext = useResetDrawingContext();

  const { onStartDrawingFromDialog } = useDrawWithViewMode(
    form,
    setShowDialog,
    'geometry',
    DrawingStyles.VirtualAton
  );

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen); // Update showDialog state

    // Make sure that we are not unmounting the component when drawing, but we will unmount it if we are not drawing
    if (!showDrawPanel) {
      setShowOuterDialog(isOpen);
    }
  };

  const handleConfirm = React.useCallback(() => {
    if (showDrawPanel) {
      resetDrawingContext();
    }
    setShowDialog(false);
    setShowOuterDialog(false);
  }, [showDrawPanel, resetDrawingContext, setShowOuterDialog]);

  const { handleClose: handleExitWarning, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleConfirm,
    title: messages.atonWarningTitle || 'Discard AtoN changes?',
    description: messages.atonWarningDescription || 'Your AtoN configuration will be lost. Are you sure you want to close?',
  });

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={handleOpenChange}
        title={dialogTitle}
        isLoading={isLoadingGroups}
        hasLoaded={!isLoadingGroups && !!groups}
        buttonId="submit-virtual-aton-button"
        buttonText={
          isSubmitting ? messages.genericSubmitting : messages.genericSubmit
        }
        buttonDisabled={isSubmitting}
        buttonOnClick={form.handleSubmit(onSubmit)}
        onClose={handleExitWarning}
      >
        <MutateVirtualForm
          form={form}
          onStartDrawing={onStartDrawingFromDialog}
          aton={aton ?? undefined}
          mutateType={mutateType}
        ></MutateVirtualForm>
      </DialogForm>
      <ExitWarningDialog />
    </Form>
  );
};
